import * as React from 'react'
import {Box} from '@mui/material'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

/**
 * Shared definition-list primitive for Weight Freeze (v2) — replaces the
 * mockup's `.wf-kv` CSS class (`grid-template-columns: auto 1fr`, muted
 * label, bold right-aligned value with tabular numerals). Six sections
 * (Catalogue, Packing, DWS, Initiate, Requests, SoP) render label/value
 * pairs, so this is built once rather than six times.
 *
 * Real `<dl>/<dt>/<dd>` semantics via MUI's `component` prop, not a grid
 * of anonymous `<div>`s — a screen reader announces a definition list's
 * term/description pairing, and every one of the six consumers is a
 * plain fact sheet a merchant reads, not an interactive widget.
 *
 * `columns` repeats the label/value column pair side by side (e.g.
 * `columns={2}` for a card with more stats than a single tall column
 * reads well). It's still one `<dl>`, so `<dt>`/`<dd>` order in the DOM
 * — and therefore what a screen reader hears — never depends on the
 * visual column count; CSS Grid auto-placement fills left-to-right,
 * top-to-bottom, so an `items` array that isn't a multiple of `columns`
 * simply leaves the last row short. It collapses to a single pair below
 * `sm`, because `columns={2}` on a phone is four tracks in 360px.
 *
 * `item.full` gives one pair the whole width, label above value and the
 * value left-aligned: a sentence of remarks read right-aligned against a
 * short label across a wide card is the one thing the shared right-aligned
 * tabular treatment gets wrong.
 */
export default function KeyValue({items, columns = 1}) {
  return (
    <Box
      component='dl'
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: 'auto minmax(0, 1fr)', sm: `repeat(${columns}, auto minmax(0, 1fr))`},
        columnGap: GAP.lg,
        rowGap: GAP.xs,
        m: 0,
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <Box
            component='dt'
            sx={{
              fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, m: 0,
              ...(item.full ? {gridColumn: '1 / -1'} : null),
            }}
          >
            {item.label}
          </Box>
          <Box
            component='dd'
            sx={{
              fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: NAVY,
              m: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
              ...(item.full ? {gridColumn: '1 / -1', textAlign: 'left', lineHeight: 1.5} : null),
            }}
          >
            {/* A pair with nothing in it reads as broken, not as "not recorded" — and against live
                data that is common: the product master has no branding or packing-type column, so
                those arrive null. An em dash is the same answer the weight and dimension formatters
                already give, so a card missing three facts looks deliberate rather than half-loaded. */}
            {item.value === null || item.value === undefined || item.value === '' ? '—' : item.value}
            {item.hint && (
              <Box
                component='span'
                sx={{
                  display: 'block', fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.regular,
                  color: SUBTLE, fontVariantNumeric: 'normal', mt: 0.25,
                }}
              >
                {item.hint}
              </Box>
            )}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  )
}
